import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { FieldType, FieldTypeConfig } from '@ngx-formly/core';
import { BehaviorSubject, Subject, combineLatest, filter, takeUntil } from 'rxjs';
import { LgvFormUtilityService } from '../../../core/services/lgv-form-utility.service';
import { getProperty, setProperty } from 'dot-prop';

@Component({
    selector: 'lgv-formly-residence-duration',
    templateUrl: './formly-residence-duration.component.html',
    styleUrls: ['./formly-residence-duration.component.scss'],
    changeDetection: ChangeDetectionStrategy.Default
})
export class FormlyResidenceDurationComponent extends FieldType<FieldTypeConfig> implements OnInit, OnDestroy {

    residenceDurationForm: UntypedFormGroup;
    onDestroy$ = new Subject();
    residenceDurationInMonths: number;

    constructor(private fb: FormBuilder,
        private formService: LgvFormUtilityService
    ) {
        super();
        this.residenceDurationForm = this.fb.group({
            years: [null, Validators.required],
            months: [null, Validators.required]
        })
    }

    ngOnInit(): void {
        var modelToUpdatePath = this.props?.modelToUpdate;
        if (this.props?.isRepeater && 'index' in this?.field?.parent) {
            const index = this?.field?.parent['index'];
            modelToUpdatePath = `${this.props?.modelToUpdate}[${index}]`;
        }

        var model = this.formService?.dataModel$?.value;
        const address = getProperty(model, modelToUpdatePath) || null;
        const totalMonths = address?.residencyDurationInMonths ?? 0;

        const currentYear = Math.floor(totalMonths / 12);
        this.residenceDurationForm.get('years').setValue(currentYear ?? 0);

        const currentMonth = totalMonths % 12;
        this.residenceDurationForm.get('months').setValue(currentMonth ?? 0);

        // this control is used for the "current address", as well as for each previous address item.  Changes to any will
        // update the total duration.  We need to watch their changes to refresh our validation
        this.formService.dataModel$.pipe(filter(f=> !!f), takeUntil(this.onDestroy$)).subscribe(()=>{
            this.residenceDurationInMonths = this.getSumDurationForAllAddresses();
        });

        this.residenceDurationForm.valueChanges.pipe()
            .subscribe((form) => {
                var modelToUpdatePath = this.props?.modelToUpdate;
                if (this.props?.isRepeater && 'index' in this?.field?.parent) {
                    const index = this?.field?.parent['index'];
                    modelToUpdatePath = `${this.props?.modelToUpdate}[${index}]`;
                }

                var dataModel = this.formService?.dataModel$?.value;
                const monthsTotal = (form?.years ?? 0) * 12 + (form?.months ?? 0);
                setProperty(dataModel, modelToUpdatePath + '.residencyDurationInMonths', monthsTotal);
                this.formService.dataModel$.next(dataModel);

                this.residenceDurationInMonths = this.getSumDurationForAllAddresses();

                this.forceFormlyRefresh();
            });
    }

    private forceFormlyRefresh() {
        // trigger expression validation since we are manually manipulating the model
        this.options.checkExpressions(this.field);

        (this.field.parent.options as any)._checkField({
          fieldGroup: this.field.parent.fieldGroup,
          model: this.field.parent.model,
          formControl: this.field.parent.form,
          options: this.field.parent.options,
        });
      }

    getSumDurationForAllAddresses() {
        let totalDurationInMonths = 0;

        var anyBorrower;
        
        if(this.getIsCoBorrower()){
            anyBorrower = this.formService?.dataModel$?.value?.loanApp?.borrower?.coBorrower;
        }else{
            anyBorrower = this.formService?.dataModel$?.value?.loanApp?.borrower;
        }

        //get sum of previous addresses
        anyBorrower?.previousAddresses?.forEach((address)=> {
            totalDurationInMonths += (+address?.residencyDurationInMonths);
        });

        totalDurationInMonths += anyBorrower?.currentAddress?.residencyDurationInMonths;
        return totalDurationInMonths;
    }

    ngOnDestroy() {
        this.onDestroy$.next(false);
        this.onDestroy$.complete();
    }

    getIsCoBorrower(){
        if(this.field?.props?.isRepeater){
            return this.field?.parent?.parent?.key?.toString().includes('.coBorrower');
        }else{
            return this.field?.key?.toString().includes('.coBorrower');
        }
    }
}
